<template>
  <div class="box">
    <!-- Header with search and add button -->
    <div class="level">
      <div class="level-left">
        <div class="level-item">
          <h3 class="title">Properties & Banking</h3>
        </div>
      </div>
      <div class="level-right">
        <div class="level-item">
          <div class="field" style="min-width: 350px; margin-right: 1rem;">
            <div class="control has-icons-left">
              <input
                v-model="searchTerm"
                class="input"
                type="text"
                placeholder="Search by address or bank account..."
              />
              <span class="icon is-left">
                <i class="fas fa-search"></i>
              </span>
            </div>
          </div>
        </div>
        <div v-if="!isAuthorizedRep" class="level-item">
          <button class="button is-primary" @click="handleAddProperty">
            <span class="icon">
              <i class="fas fa-plus"></i>
            </span>
            <span>Add Property</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="table-container">
      <table class="table is-fullwidth is-hoverable">
        <thead>
          <tr>
            <th>Address</th>
            <th>Bank Account</th>
            <th>Authorized Representative</th>
            <th>Property Status</th>
            <th v-if="!isAuthorizedRep"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="property in filteredProperties"
            :key="property.rid"
          >
            <td><strong>{{ property.property_address }}</strong></td>
            <td class="medium-gray">
              {{ property.bank_name }}<br>
              <small>{{ property.bank_number }}</small>
            </td>
            <td>
              <div v-if="property.ar_name">
                {{ property.ar_name }}<br>
                <small class="medium-gray">{{ property.ar_email }}</small>
              </div>
              <span v-else class="medium-gray">-</span>
            </td>
            <td>
              <span
                class="tag is-rounded"
                :style="{
                  backgroundColor: getPropertyStatusInfo(property.property_status).bgColor,
                  color: getPropertyStatusInfo(property.property_status).color,
                  fontWeight: 600
                }"
              >
                {{ getPropertyStatusInfo(property.property_status).label }}
              </span>
            </td>
            <td v-if="!isAuthorizedRep" class="has-text-right">
              <button
                class="button is-primary is-small"
                @click.stop="editProperty(property)"
              >
                <span class="icon">
                  <i class="fas fa-edit"></i>
                </span>
                <span>Edit</span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- No results message -->
      <div v-if="filteredProperties.length === 0" class="has-text-centered" style="padding: 3rem;">
        <p class="medium-gray">
          <i class="fas fa-building fa-3x" style="color: #cfcfcf; margin-bottom: 1rem;"></i><br>
          No properties found
        </p>
      </div>
    </div>

    <!-- Add Property Modal -->
    <add-property-modal
      :is-active="showAddModal"
      @close="showAddModal = false"
      @submit="handlePropertyAdded"
    />

    <!-- Edit Property Modal -->
    <add-property-modal
      :is-active="showEditModal"
      :edit-mode="true"
      :property-data="propertyToEdit"
      @close="showEditModal = false; propertyToEdit = null"
      @submit="handlePropertyUpdated"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import AddPropertyModal from "./AddPropertyModal.vue";

export default {
  name: "PropertiesTable",
  components: {
    AddPropertyModal,
  },
  data() {
    return {
      searchTerm: "",
      showAddModal: false,
      showEditModal: false,
      propertyToEdit: null,
    };
  },
  computed: {
    ...mapGetters("auth", ["userInfo"]),
    ...mapGetters("dashboard", ["properties", "authorizedReps"]),
    filteredProperties() {
      if (!this.searchTerm) {
        return this.properties;
      }
      return this.properties.filter(property => {
        const searchLower = this.searchTerm.toLowerCase();
        return (
          property.property_address?.toLowerCase().includes(searchLower) ||
          property.bank_name?.toLowerCase().includes(searchLower) ||
          property.bank_number?.toLowerCase().includes(searchLower)
        );
      });
    },
    isAuthorizedRep() {
      return this.userInfo?.qbUser?.role === "ar";
    },
  },
  methods: {
    getPropertyStatusInfo(status) {
      const statusMap = {
        'AR Validated': { label: 'AR Validated', color: '#3a833c', bgColor: '#dcf8d8' }, // Phanatic Green - Success
        'Property Auth Form Required': { label: 'Property Auth Form Required', color: '#f99300', bgColor: '#fff7d0' }, // Flyers Orange/Bell Yellow - Warning
        'Approved': { label: 'Approved', color: '#3a833c', bgColor: '#dcf8d8' }, // Phanatic Green - Success
        'Pending Review': { label: 'Pending Review', color: '#444444', bgColor: '#f0f0f0' }, // Dark Gray/Ghost Gray - Neutral
      };
      return statusMap[status] || { label: status, color: '#444444', bgColor: '#f0f0f0' };
    },
    editProperty(property) {
      console.log("Edit property:", property);
      this.propertyToEdit = { ...property }; // Create a copy to avoid direct mutation
      this.showEditModal = true;
    },
    handleAddProperty() {
      this.showAddModal = true;
    },
    handlePropertyAdded(newProperty) {
      console.log("New property added:", newProperty);
      // Refresh properties from server
      this.$store.dispatch('dashboard/fetchProperties');
      this.showAddModal = false;
      this.$emit("property-added", newProperty);
    },
    handlePropertyUpdated(updatedProperty) {
      console.log("Property updated:", updatedProperty);
      // Refresh properties from server
      this.$store.dispatch('dashboard/fetchProperties');
      this.showEditModal = false;
      this.propertyToEdit = null;
      this.$emit("property-updated", updatedProperty);
    },
  },
};
</script>

<style scoped>
/* Custom styling to match PhilaUI */
.medium-gray {
  color: #a1a1a1; /* Medium Gray - Philly Style Guide */
}
</style>
